/**
 * The incoming-call ring, synthesised with Web Audio, so there's no sound
 * file and nothing brand-specific. Two short bursts of 400+450 Hz, then a
 * pause, like a traditional phone.
 */
export class Ringer {
  private context: AudioContext | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  /** Starts ringing until stop(). Browsers only allow sound after the user has clicked on the page. */
  start(): void {
    if (this.timer) return;
    this.context ??= new AudioContext();
    void this.context.resume();
    this.burstPair();
    this.timer = setInterval(() => this.burstPair(), 3000);
  }

  /** What the caller hears while the other side rings. */
  startRingback(): void {
    if (this.timer) return;
    this.context ??= new AudioContext();
    void this.context.resume();
    const once = () => this.context && this.burst(this.context, this.context.currentTime + 0.05, 1, [425], 0.06);
    once();
    this.timer = setInterval(once, 5000);
  }

  /** Stops at once, including any ring already scheduled. */
  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    void this.context?.suspend();
  }

  /** One ring, for checking the speakers. */
  test(): void {
    this.context ??= new AudioContext();
    void this.context.resume();
    this.burstPair();
  }

  private burstPair(): void {
    const context = this.context;
    if (!context) return;
    const start = context.currentTime + 0.05;
    for (const offset of [0, 0.6]) this.burst(context, start + offset, 0.4);
  }

  private burst(context: AudioContext, at: number, length: number, frequencies = [400, 450], volume = 0.12): void {
    const gain = context.createGain();
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(volume, at + 0.02);
    gain.gain.setValueAtTime(volume, at + length - 0.03);
    gain.gain.linearRampToValueAtTime(0, at + length);
    gain.connect(context.destination);
    for (const frequency of frequencies) {
      const tone = context.createOscillator();
      tone.frequency.value = frequency;
      tone.connect(gain);
      tone.start(at);
      tone.stop(at + length);
    }
  }
}

export const ringer = new Ringer();
